<template>
  <div
    class="final-temperature-chart avoid-break"
  >
    <div class="text-center">
      <strong v-show="showTitle">
        {{ $t('general.winding') }} {{ windingId }}
      </strong>
    </div>
    <div v-if="hasDataforWinding">
      <e-chart
        class="final-temperature-chart__chart"
        autoresize
        ref="finalTemperatureChart"
        :options="options"
        @rendered="onRendered"
      />
      <img
        class="final-temperature-chart__image w-100 avoid-break"
        alt="final-temperature-chart-image"
        :src="chartImg"
      >
    </div>
    <s-empty-list v-else />
  </div>
</template>

<script>
import themeService from '@/services/themeService';
import engineeringQuickChartsMixin
  from '@/domain/mixins/installation/engineering/engineeringQuickChartsMixin';
import { FinalGradientGraphicVariables } from 'sigma-mappings';

const UNIT = '°C';

export default {
  name: 'FinalTemperatureChart',
  props: {
    windingId: {
      type: Number,
      required: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [engineeringQuickChartsMixin],
  data: () => ({
    unit: UNIT,
  }),
  computed: {
    hasDataforWinding() {
      return this.chartData.windingId;
    },
    notation() {
      const [firstValue] = this.chartData.variables || [];
      return (firstValue || {}).notation || '';
    },
    options() {
      const series = this.getChartSeries();
      return {
        backgroundColor: this.bgColor,
        tooltip: {
          show: true,
          confine: false,
          trigger: 'item',
          borderColor: themeService.getStyleVar('color-tooltip-border'),
          formatter: this.getTooltipFormatter,
          axisPointer: {
            type: 'cross',
            label: {
              show: false,
            },
            crossStyle: {
              opacity: 0,
            },
          },
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontSize: this.labelSize,
          },
          axisLine: {
            show: true,
            onZero: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
        },
        yAxis: {
          name: this.notation,
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
          splitNumber: 3,
          axisLabel: {
            show: true,
            fontSize: this.labelSize,
            color: this.fontColor,
            showMinLabel: false,
            formatter: `{value} ${this.unit}`,
          },
          splitLine: {
            show: false,
          },
        },
        grid: {
          top: this.notation ? 40 : 30,
          bottom: 40,
          right: 30,
          left: 50,
        },
        series,
      };
    },
  },
  methods: {
    getLineColorByName(name) {
      const variables = {
        [FinalGradientGraphicVariables.TRANSFORMER_OIL_TEMPERATURE]: themeService
          .getStyleVar('chart-color-gray'),
        [FinalGradientGraphicVariables.WINDING_TEMPERATURE]: themeService
          .getPrimaryColor(),
        [FinalGradientGraphicVariables.FINAL_WINDING_TEMPERATURE]: themeService
          .getStyleVar('chart-color-blue'),
        [FinalGradientGraphicVariables.SHUTDOWN_TEMPERATURE]: `${themeService
          .getStyleVar('color-danger')}8c`,
        [FinalGradientGraphicVariables.ALARM_TEMPERATURE]: `${themeService
          .getStyleVar('color-secondary')}8c`,
      };
      return variables[name] || themeService.getPrimaryColor();
    },
    onRendered() {
      this.$nextTick(() => {
        this.chartImg = this.isCollapsed || !this.$refs.finalTemperatureChart.height
          ? '#'
          : this.$refs.finalTemperatureChart.getDataURL();
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .final-temperature-chart
    display: block
    padding-top: 5px
    &__chart
      border: 1px solid var(--color-border-dark)
      border-radius: 5px
      min-height: 210px
      height: max-content
      margin: 10px 0
      width: 100%
    &__image
      display: none
    @media print
      .final-temperature-chart
        text-align: center
        &__chart
          display: none
        &__image
          display: block
</style>
